import React from 'react';

// DJB-252: openDays is null (days unknown) or 7 booleans, Monday → Sunday,
// true = open. Labels follow the same index.
export const OPEN_DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export const hasKnownOpenDays = (openDays) =>
  Array.isArray(openDays) && openDays.length === OPEN_DAY_LABELS.length;

// Edit starts from the stored days, or all closed (red) when unknown
export const toOpenDaysDraft = (openDays) =>
  hasKnownOpenDays(openDays) ? openDays.map(Boolean) : OPEN_DAY_LABELS.map(() => false);

// Saving with every day closed stores null — back to "days unknown". DJB-255:
// a reset draft is null itself and saves as null too
export const fromOpenDaysDraft = (draft) =>
  hasKnownOpenDays(draft) && draft.some(Boolean) ? draft : null;

// Same stored value? Unknown (null / missing / malformed) only equals
// unknown; two known lists must match day by day. Used to skip the workdays
// request when a save leaves the days untouched.
export const openDaysEqual = (a, b) => {
  const knownA = hasKnownOpenDays(a);
  const knownB = hasKnownOpenDays(b);
  if (!knownA || !knownB) return knownA === knownB;
  return a.every((open, i) => Boolean(open) === Boolean(b[i]));
};

// DJB-255: toggling a reset (unknown) draft starts again from all closed
export const toggleOpenDay = (draft, index) =>
  toOpenDaysDraft(draft).map((open, i) => (i === index ? !open : open));

// DJB-255: index (0 = Ma … 6 = Zo) of today in Europe/Brussels, the same
// timezone the server uses for todayStatus
const BRUSSELS_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const getBrusselsTodayIndex = (date = new Date()) =>
  BRUSSELS_WEEKDAYS.indexOf(
    new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Brussels', weekday: 'short' }).format(date)
  );

// Seven round dots. Read-only unless onToggle is given; unknown days render
// all grey with a "Dagen niet gekend" caption. DJB-255: todayIndex puts a
// thin ring around that day's dot.
export const OpenDaysDots = ({ openDays, onToggle, todayIndex }) => {
  const known = hasKnownOpenDays(openDays);
  return (
    <div className="open-days">
      <div className="open-days-dots" role="group" aria-label="Openingsdagen">
        {OPEN_DAY_LABELS.map((label, index) => {
          const state = !known ? 'unknown' : openDays[index] ? 'open' : 'closed';
          const isToday = index === todayIndex;
          const title = `${label}: ${state === 'open' ? 'open' : state === 'closed' ? 'gesloten' : 'niet gekend'}${isToday ? ' (vandaag)' : ''}`;
          return onToggle ? (
            <button
              key={label}
              type="button"
              className={`open-day-dot ${state} editable${isToday ? ' is-today' : ''}`}
              aria-pressed={state === 'open'}
              aria-label={title}
              title={title}
              onClick={() => onToggle(index)}
            >
              {label}
            </button>
          ) : (
            <span
              key={label}
              className={`open-day-dot ${state}${isToday ? ' is-today' : ''}`}
              title={title}
              data-state={state}
              aria-current={isToday ? 'date' : undefined}
            >
              {label}
            </span>
          );
        })}
      </div>
      {/* DJB-255: a reset in edit mode shows the caption too */}
      {!known && <p className="open-days-unknown">Dagen niet gekend</p>}
    </div>
  );
};

// DJB-253: todayStatus is computed server-side (Europe/Brussels); anything
// missing or unexpected reads as unknown
export const TODAY_STATUS_LABELS = { open: 'Open', closed: 'Closed', unknown: 'Unknown' };
export const TODAY_STATUS_ORDER = ['open', 'closed', 'unknown'];

export const normalizeTodayStatus = (todayStatus) => {
  const status = String(todayStatus || '').toLowerCase();
  return TODAY_STATUS_LABELS[status] ? status : 'unknown';
};
